import { Copy, SquarePen, TriangleAlert } from "lucide-react";
import type { JSX } from "react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { V3Button } from "@/components/ui/v3/button";
import { V3Chip } from "@/components/ui/v3/chip";
import { cn } from "@/lib/utils";
import { usePhoneLayout } from "../layout";
import { VerifyGateProvider } from "../VerifyReview";
import { useDocumentControls } from "./DocumentControls";

// The note's scroll container (VisitWorkspaceV3).
export const NOTE_SCROLLER_ID = "visit-workspace-main";

export interface CopyToChart {
  onCopy: () => void;
  disabled: boolean;
  // The shown document has no text yet (an empty pre-chart draft).
  empty?: boolean;
}

export type CopyToChartEmphasis = "primary" | "secondary" | "hidden";

// One ink primary per state of the visit (design review 04/11): Copy to
// chart is the finish line, so it is the primary only once the note is
// ready with nothing left to review. Before that the state's own next step
// is: Start visit on a pre-chart draft, Review while values are flagged
// (the Needs review banner). While the note is written Copy stays a
// disabled secondary; an empty draft has nothing to copy, so no Copy.
// While a card on the note is open (review card, sentence editor) its own
// button is the primary.
export const copyToChartEmphasis = ({
  preChart,
  empty,
  disabled,
  reviewCount,
  overlay = false,
}: {
  preChart: boolean;
  empty: boolean;
  disabled: boolean;
  reviewCount: number;
  overlay?: boolean;
}): CopyToChartEmphasis => {
  if (preChart) return empty ? "hidden" : "secondary";
  if (disabled || reviewCount > 0 || overlay) return "secondary";
  return "primary";
};

// "Needs review · N" (N on phones) while values are flagged; opens the
// first one.
const ReviewChip = (): JSX.Element | null => {
  const { t } = useTranslation();
  const controls = useDocumentControls();
  const count = controls?.reviewCount ?? 0;
  if (count === 0) return null;
  return (
    <V3Chip
      aria-label={t("visit.workspace.verify.badgeAria", { count })}
      className="mr-1 max-lg:h-8"
      data-testid="note-actions-review"
      disabled={!controls?.openReview}
      size="sm"
      title={t("visit.workspace.verify.tooltip")}
      variant="warn"
      onClick={() => controls?.openReview?.()}
    >
      <TriangleAlert aria-hidden />
      <span aria-hidden className="max-sm:hidden">
        {t("visit.workspace.verify.badge", { count })}
      </span>
      <span aria-hidden className="sm:hidden">
        {count}
      </span>
    </V3Chip>
  );
};

// Edit, Export, Create ▾ and Copy to chart for the open note (clinician feedback:
// within reach of the note, not across the screen). They sit on the note's
// meta line and move to the top bar once that line scrolls away, with the
// "Needs review · N" chip there (`inTopBar`; Export then goes to "…" on
// phones). Hidden while the note is edited (the editor has its own Save
// bar).
export const NoteActions = ({
  create,
  copyToChart,
  preChart = false,
  inTopBar = false,
}: {
  // Create ▾ (new note, referral letter, patient summary).
  create: React.ReactNode;
  // Runs the "[verify]" review before copying.
  copyToChart: CopyToChart;
  // A pre-chart draft: Start visit is the primary, not Copy.
  preChart?: boolean;
  inTopBar?: boolean;
}): JSX.Element | null => {
  const { t } = useTranslation();
  const controls = useDocumentControls();
  // Phones: in the top bar, Export is in "…" (VisitTopBar) so the row fits.
  const phone = usePhoneLayout();
  if (controls?.editing) return null;
  const showExport = !!controls?.exportAction && !(inTopBar && phone);
  const copyEmphasis = copyToChartEmphasis({
    preChart,
    empty: !!copyToChart.empty,
    disabled: copyToChart.disabled,
    reviewCount: controls?.reviewCount ?? 0,
    overlay: !!controls?.overlay,
  });
  return (
    <div
      aria-label={t("visit.workspace.noteActions")}
      className={cn(
        "flex shrink-0 items-center gap-0.5",
        inTopBar &&
          "animate-in fade-in slide-in-from-top-1 duration-150 motion-reduce:animate-none",
      )}
      data-placement={inTopBar ? "top-bar" : "meta-line"}
      data-testid="note-actions"
      role="toolbar"
    >
      {inTopBar && <ReviewChip />}
      {controls && (
        // Visible Edit (clinician feedback 03/11): the label from 640px, the
        // pencil alone on phones. "…" → Edit note and a click on the text
        // open the same editor.
        <V3Button
          aria-label={t("v3.workspace.editNote")}
          className="max-sm:w-11 max-sm:px-0"
          data-testid="note-edit"
          disabled={!controls.canEdit}
          title={t("v3.workspace.editNote")}
          variant="ghost"
          onClick={() => controls.startEditing()}
        >
          <SquarePen aria-hidden />
          <span aria-hidden className="max-sm:hidden">
            {t("v3.workspace.edit")}
          </span>
        </V3Button>
      )}
      {showExport && (
        <VerifyGateProvider value={controls?.gate ?? null}>
          {controls.exportAction}
        </VerifyGateProvider>
      )}
      {create}
      {copyEmphasis !== "hidden" && (
        <V3Button
          aria-keyshortcuts="Shift+Meta+C Shift+Control+C"
          aria-label={t("visit.workspace.copyToChart")}
          className="ml-1 max-sm:px-3.5"
          data-emphasis={copyEmphasis}
          data-testid="copy-to-chart"
          disabled={copyToChart.disabled}
          title={t("visit.workspace.copyToChartTooltip")}
          variant={copyEmphasis}
          onClick={copyToChart.onCopy}
        >
          <Copy aria-hidden />
          <span aria-hidden className="max-sm:hidden">
            {t("visit.workspace.copyToChart")}
          </span>
          <span aria-hidden className="sm:hidden">
            {t("visit.workspace.copyShort")}
          </span>
        </V3Button>
      )}
    </div>
  );
};

// Reports when the element has scrolled out of the note's scroller (and
// false again when it is back or unmounted). The note actions and the
// pre-chart's Start visit use it to move into the top bar: one copy of an
// action on screen at a time.
// `coveredBelow`: px at the scroller's bottom that something sits over (the
// Ask dock); with `half`, the element is away unless at least half of it
// shows above that (a mostly covered button is not one to press).
export const useScrolledAway = <T extends HTMLElement>(
  onScrolledAway: (away: boolean) => void,
  {
    coveredBelow = 0,
    half = false,
  }: { coveredBelow?: number; half?: boolean } = {},
): React.RefObject<T | null> => {
  const element = useRef<T>(null);
  const report = useRef(onScrolledAway);
  report.current = onScrolledAway;
  useEffect(() => {
    const target = element.current;
    if (!target || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const shown = half
          ? entry.isIntersecting && entry.intersectionRatio >= 0.5
          : entry.isIntersecting;
        report.current(!shown);
      },
      {
        root: document.getElementById(NOTE_SCROLLER_ID),
        rootMargin: `0px 0px ${-Math.max(0, Math.round(coveredBelow))}px 0px`,
        threshold: half ? [0, 0.5] : 0,
      },
    );
    observer.observe(target);
    return () => {
      observer.disconnect();
      report.current(false);
    };
  }, [coveredBelow, half]);
  return element;
};

// The note's meta line above the title: "date · template · duration" with
// the note's actions on its right (no extra row, so the note starts no
// lower). It reports when it has scrolled out of the note's scroller, so
// the actions move to the top bar: one row of actions at a time.
export const NoteMetaBar = ({
  meta,
  actions,
  onScrolledAway,
}: {
  meta: string;
  // NoteActions, or nothing while they are in the top bar.
  actions: React.ReactNode;
  onScrolledAway: (away: boolean) => void;
}): JSX.Element => {
  const line = useScrolledAway<HTMLDivElement>(onScrolledAway);
  return (
    <div
      ref={line}
      className="mb-1 flex min-h-11 items-center gap-1"
      data-testid="note-meta-bar"
    >
      <p
        // Phones: up to two lines beside the actions, so the date and
        // template read in full instead of "11:32 …".
        className="min-w-0 flex-1 truncate text-[13px] tabular-nums text-v3-text-3 max-sm:line-clamp-2 max-sm:whitespace-normal max-sm:text-[12.5px] max-sm:leading-[17px]"
        data-testid="visit-meta"
      >
        {meta}
      </p>
      {actions}
    </div>
  );
};
